import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormControlLabel, Radio, RadioGroup } from '@mui/material';

import { Dialog } from 'components/shared/index';
import { SmartAction } from 'components/shared/DialogProvider/dialogs/SmartActionDialog/components/SmartAction/SmartAction';
import { SmartActionState } from 'components/shared/DialogProvider/dialogs/SmartActionDialog/SmartActionDialog.types';
import { ContentContainer } from 'components/shared/DialogProvider/dialogs/SmartActionDialog/SmartActionDialog.styles';
import { ActionsContainer } from 'components/shared/DialogProvider/dialogs/styles';

import { EditAppointmentTypeResponse } from 'generated/index';

type RecurrenceConfirmationDialogProps = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: (editAppointmentType: EditAppointmentTypeResponse) => void;
  onCancel: () => void;
};

export default function RecurrenceConfirmationDialog({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel
}: RecurrenceConfirmationDialogProps) {
  const { t } = useTranslation('common');
  const [selectedEditType, setSelectedEditType] =
    useState<EditAppointmentTypeResponse | null>(null);

  const handleConfirm = () => {
    if (selectedEditType) {
      onConfirm(selectedEditType);
      setSelectedEditType(null);
    }
  };

  const handleCancel = () => {
    setSelectedEditType(null);
    onCancel();
  };

  return (
    <Dialog open={open} handleClose={handleCancel} title={title}>
      <ContentContainer>
        <SmartAction state={SmartActionState.ACTIVE} title={description}>
          <RadioGroup
            aria-labelledby="recurrence-confirmation-radio-buttons-group"
            value={selectedEditType ?? ''}
            onChange={(_, value) =>
              setSelectedEditType(value as EditAppointmentTypeResponse)
            }
          >
            <FormControlLabel
              value="ONLY_THIS"
              control={<Radio autoFocus />}
              label={t(
                'appointmentsOverview.editParticipants.recurrenceDialog.onlyThis'
              )}
            />
            <FormControlLabel
              value="THIS_AND_ALL_SUBSEQUENT"
              control={<Radio />}
              label={t(
                'appointmentsOverview.editParticipants.recurrenceDialog.thisAndSubsequent'
              )}
            />
          </RadioGroup>
        </SmartAction>

        <ActionsContainer>
          <Button variant="text" fullWidth onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button
            fullWidth
            disabled={!selectedEditType}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </ActionsContainer>
      </ContentContainer>
    </Dialog>
  );
}
